// SPA navigation detection.
//
// History API mutations don't fire any standard event, so we patch pushState
// and replaceState to dispatch a synthetic 'amb:locationchange' that we also
// fire from popstate. The patch is idempotent — re-running init won't
// double-wrap.
//
// bfcache: pageshow with persisted === true means the page was restored from
// the back/forward cache. We treat that as a new view (fresh view_id, new
// page_view event) because all the in-memory state of the previous view is
// still live but the user perceives it as a "new page".

import { EVENT_TYPES } from '../schema.js';
import { now } from '../safe.js';
import { newView, getIdentity, flushNewFlags } from '../identity.js';

const PATCHED = '__amb_history_patched__';

export function initSpa(emit, getRouteName) {
  patchHistory();

  window.addEventListener('amb:locationchange', () => onRoute(emit, getRouteName));
  window.addEventListener('popstate', () => onRoute(emit, getRouteName));
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) onBfcacheRestore(emit, getRouteName);
  });

  // Initial page_view fires explicitly from the loader after init wires up.
}

export function emitPageView(emit, getRouteName, opts) {
  opts = opts || {};
  const id = getIdentity();
  if (!id) return;
  emit({
    id: id.viewId + ':pv',
    t: now(),
    tp: EVENT_TYPES.PAGE_VIEW,
    n: 'page_view',
    mv: 0,
    mu: '',
    mr: '',
    mnt: opts.navigationType || '',
    dim: {
      route: (typeof getRouteName === 'function' && getRouteName()) || '',
      bfcache: opts.bfcache ? '1' : '0',
      spa: opts.spa ? '1' : '0'
    },
    met: {}
  });
  // Once the page_view is in the buffer, the is_new_* flags have served their
  // purpose. Clearing them prevents follow-up events on the same session from
  // being mis-labeled.
  flushNewFlags();
}

function onRoute(emit, getRouteName) {
  newView();
  emitPageView(emit, getRouteName, { spa: 1, navigationType: 'spa' });
}

function onBfcacheRestore(emit, getRouteName) {
  newView({ documentNavigation: true });
  emitPageView(emit, getRouteName, { bfcache: 1, navigationType: 'back-forward-cache' });
}

function patchHistory() {
  if (window[PATCHED]) return;
  window[PATCHED] = true;
  try {
    const h = window.history;
    const ps = h.pushState;
    const rs = h.replaceState;
    h.pushState = function () {
      const r = ps.apply(this, arguments);
      try { window.dispatchEvent(new Event('amb:locationchange')); } catch (_) {}
      return r;
    };
    h.replaceState = function () {
      const r = rs.apply(this, arguments);
      try { window.dispatchEvent(new Event('amb:locationchange')); } catch (_) {}
      return r;
    };
  } catch (_) { /* silent */ }
}
